import type { TextDirection } from "@yjsgames/i18n";

/** chip 내부(아이콘·라벨)의 chip 좌측 기준 상대 좌표. */
export interface HudChipInner {
  /** chip 좌측 기준 아이콘 중심 x. */
  readonly iconX: number;
  /** chip 좌측 기준 라벨 기준점 x. */
  readonly labelX: number;
  /** 라벨 origin x — 0(좌측 정렬) | 1(우측 정렬). */
  readonly labelOriginX: number;
}

export interface HudLayout {
  /** 설정 기어 버튼 중심 x (origin 0.5). LTR=우측 끝, RTL=좌측 끝. */
  readonly settingsBtnX: number;
  /** 각 chip 좌측 x (origin 0,0) — **논리 순서**(idx 0=gold). LTR 좌→우, RTL 우→좌. */
  readonly chipXs: readonly number[];
  readonly chipW: number;
  readonly inner: HudChipInner;
}

export interface HudLayoutSpec {
  readonly dir: TextDirection;
  readonly x: number;
  readonly barW: number;
  readonly sidePad: number;
  readonly settingsSize: number;
  readonly chipGap: number;
  readonly chipCount: number;
  readonly chipPadX: number;
  readonly iconSize: number;
}

/**
 * 상단 HUD 좌표를 방향에 맞춰 계산하는 순수 함수 (Phaser 비의존 → 단위테스트로 양방향 검증).
 * LTR 값은 기존 HudView 하드코딩과 동일(회귀 0). RTL이면 설정버튼·chip 순서·chip 내부를 좌우 미러링.
 */
export function computeHudLayout(spec: HudLayoutSpec): HudLayout {
  const { dir, x, barW, sidePad, settingsSize, chipGap, chipCount, chipPadX, iconSize } = spec;
  const rtl = dir === "rtl";

  // 설정 버튼: LTR=우측 끝, RTL=좌측 끝.
  const settingsBtnX = rtl
    ? x + sidePad + settingsSize / 2
    : x + barW - sidePad - settingsSize / 2;

  // chip 영역: 설정 버튼 반대편부터 bar 가장자리까지.
  let areaLeft: number;
  let areaRight: number;
  if (rtl) {
    areaLeft = settingsBtnX + settingsSize / 2 + chipGap;
    areaRight = x + barW - sidePad;
  } else {
    areaLeft = x + sidePad;
    areaRight = settingsBtnX - settingsSize / 2 - chipGap;
  }
  const areaW = areaRight - areaLeft;
  const chipW = Math.floor((areaW - chipGap * (chipCount - 1)) / chipCount);

  // chip 좌측 x — 논리 순서(idx 0=gold). LTR 좌→우, RTL 우→좌(idx 0이 우측 끝).
  const chipXs: number[] = [];
  for (let i = 0; i < chipCount; i++) {
    const slot = i * (chipW + chipGap);
    chipXs.push(rtl ? areaRight - slot - chipW : areaLeft + slot);
  }

  // chip 내부: LTR=아이콘 좌+라벨 우(origin 0), RTL=아이콘 우+라벨 좌(origin 1).
  const inner: HudChipInner = rtl
    ? {
        iconX: chipW - chipPadX - iconSize / 2,
        labelX: chipW - chipPadX - iconSize - 4,
        labelOriginX: 1,
      }
    : {
        iconX: chipPadX + iconSize / 2,
        labelX: chipPadX + iconSize + 4,
        labelOriginX: 0,
      };

  return { settingsBtnX, chipXs, chipW, inner };
}
